import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { ADMIN_USER_ROLE_KEYS, AdminUserRoleKey } from '@chiroup/core/types/AdminUserRole.type';
import { useForm } from '@chiroup/hooks/useForm';
import React, { use, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import useAdminMe from '../../hooks/useAdminMe';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import useAdminUsers, {
  AdminUserRow,
  UpdateAdminUserBody,
  useDeleteAdminUser,
  useResetAdminUserMfa,
  useResetAdminUserPassword,
  useUpdateAdminUser,
} from '../../hooks/useAdminUsers';
import LoadingPage from '../common/LoadingPage';
import Header from '../layout/Header';
import SectionContainer from '../layout/SectionContainer';
import SectionHeader from '../layout/SectionHeader';

const ROLE_LABELS: Record<AdminUserRoleKey, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  developer: 'Developer',
  release_manager: 'Release Manager',
  tech_support: 'Tech Support',
  customer_support: 'Customer Support',
  customer_success: 'Customer Success',
  marketing: 'Marketing',
  finance: 'Finance',
  researcher: 'Researcher',
};

const STATUS_OPTIONS = [
  { value: 'active', text: 'Active' },
  { value: 'disabled', text: 'Disabled' },
];

type FormValues = {
  firstName: string;
  lastName: string;
  roleKey: AdminUserRoleKey;
  status: 'invited' | 'active' | 'disabled';
};

const validation = {
  firstName: {
    required: { message: 'First name is required.' },
  },
  lastName: {
    required: { message: 'Last name is required.' },
  },
  roleKey: {
    required: { message: 'Role is required.' },
  },
  status: {
    required: { message: 'Status is required.' },
  },
};

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { createToast } = use(ToastContext);

  const { data: meData } = useAdminMe();
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('admin_users.view');
  const { data: usersData, isFetching } = useAdminUsers(canView);

  const user: AdminUserRow | undefined = usersData?.items.find(u => u.ID === Number(id));

  const update = useUpdateAdminUser();
  const deleteUser = useDeleteAdminUser();
  const resetPassword = useResetAdminUserPassword();
  const resetMfa = useResetAdminUserMfa();

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmResetPassword, setConfirmResetPassword] = useState(false);
  const [confirmResetMfa, setConfirmResetMfa] = useState(false);

  const isSelf = meData?.adminUserId === user?.ID;
  const canManage = hasPermission('admin_users.manage');
  const canAssignSuperAdmin = hasPermission('admin_users.assign_role');
  const canResetPassword = hasPermission('admin_users.reset_password');
  const canResetMfa = hasPermission('admin_users.reset_mfa');

  const allowedRoles = ADMIN_USER_ROLE_KEYS.filter(
    r => r !== 'super_admin' || canAssignSuperAdmin
  ).map(r => ({ value: r, text: ROLE_LABELS[r] }));

  const { value, registerSubmit, isDirty, errors, onChange } = useForm<FormValues>(
    {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      roleKey: user?.roleKey ?? 'customer_support',
      status: user?.status ?? 'active',
    },
    validation
  );

  const onSubmit = async (body: Partial<FormValues>) => {
    if (!user) return;
    setSaving(true);
    const updateBody: UpdateAdminUserBody = {
      firstName: body.firstName,
      lastName: body.lastName,
      roleKey: body.roleKey,
      status: body.status === 'invited' ? undefined : body.status,
    };
    await update.mutateAsync({ id: user.ID, body: updateBody });
    setSaving(false);
  };

  const onSaveSuccess = () => {
    createToast({
      title: 'Admin user updated',
      description: null,
      type: ToastTypes.Success,
      duration: 5000,
    });
  };

  const onSaveFail = (err: any) => {
    setSaving(false);
    createToast({
      title: 'Failed to update admin user',
      description: err?.response?.data?.message || err?.message || 'An error occurred.',
      type: ToastTypes.Fail,
      duration: 5000,
    });
  };

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      await deleteUser.mutateAsync(user.ID);
      createToast({
        title: 'Admin user deleted',
        description: null,
        type: ToastTypes.Success,
        duration: 5000,
      });
      navigate('/admin-users');
    } catch (err: any) {
      setDeleting(false);
      setConfirmDelete(false);
      createToast({
        title: 'Failed to delete admin user',
        description: err?.response?.data?.message || err?.message || 'An error occurred.',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    setResettingPassword(true);
    try {
      await resetPassword.mutateAsync(user.ID);
      createToast({
        title: 'Password reset email sent',
        description: null,
        type: ToastTypes.Success,
        duration: 5000,
      });
      setConfirmResetPassword(false);
    } catch (err: any) {
      createToast({
        title: 'Failed to reset password',
        description: err?.response?.data?.message || err?.message || 'An error occurred.',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const handleResetMfa = async () => {
    if (!user) return;
    setResettingMfa(true);
    try {
      await resetMfa.mutateAsync(user.ID);
      createToast({
        title: 'MFA reset successfully',
        description: null,
        type: ToastTypes.Success,
        duration: 5000,
      });
      setConfirmResetMfa(false);
    } catch (err: any) {
      createToast({
        title: 'Failed to reset MFA',
        description: err?.response?.data?.message || err?.message || 'An error occurred.',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    } finally {
      setResettingMfa(false);
    }
  };

  if (permissionsLoading || (isFetching && !user)) {
    return <LoadingPage />;
  }

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <p className="text-gray-500">You do not have permission to manage admin users.</p>
        <button
          onClick={() => navigate('/admin-users')}
          className="text-primary-600 hover:text-primary-500 text-sm font-medium"
        >
          Back to Admin Users
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <p className="text-gray-500">Admin user not found.</p>
        <button
          onClick={() => navigate('/admin-users')}
          className="text-primary-600 hover:text-primary-500 text-sm font-medium"
        >
          Back to Admin Users
        </button>
      </div>
    );
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  return (
    <>
      <Header title={fullName} />

      {/* Edit form */}
      <SectionContainer className="sm:shadow border border-gray-300 dark:border-dark-gray-800">
        <SectionHeader title="Profile" subtitle="Edit this admin user's name, role, and status." />
        <div className="px-4 py-5 sm:px-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              name="firstName"
              label="First name *"
              value={value.firstName}
              onChange={onChange('firstName')}
              errors={errors.fieldErrors?.firstName}
              disabled={!canManage}
            />
            <Input
              name="lastName"
              label="Last name *"
              value={value.lastName}
              onChange={onChange('lastName')}
              errors={errors.fieldErrors?.lastName}
              disabled={!canManage}
            />
          </div>
          <Select
            name="roleKey"
            label="Role *"
            value={value.roleKey}
            onChange={onChange('roleKey')}
            errors={errors.fieldErrors?.roleKey}
            options={allowedRoles}
            disabled={!canManage || isSelf}
          />
          {isSelf && (
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              You cannot change your own role or status.
            </p>
          )}
          <Select
            name="status"
            label="Status *"
            value={value.status}
            onChange={onChange('status')}
            errors={errors.fieldErrors?.status}
            options={STATUS_OPTIONS}
            disabled={!canManage || isSelf}
          />
          {canManage && (
            <div className="flex justify-end pt-2">
              <button
                onClick={registerSubmit(onSubmit, { onSuccess: onSaveSuccess, onFail: onSaveFail })}
                disabled={!isDirty || saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </SectionContainer>

      {/* Account info */}
      <SectionContainer className="sm:shadow border border-gray-300 dark:border-dark-gray-800">
        <SectionHeader title="Account info" subtitle="Read-only details." />
        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-dark-gray-400">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-dark-gray-100">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-dark-gray-400">
                MFA enrolled
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-dark-gray-100">
                {user.mfaEnrolled ? 'Yes' : 'No'}
              </dd>
            </div>
            {user.lastLoginAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-dark-gray-400">
                  Last login
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-dark-gray-100">
                  {user.lastLoginAt}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-dark-gray-400">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-dark-gray-100">
                {user.createDate}
              </dd>
            </div>
          </dl>
        </div>
      </SectionContainer>

      {/* Actions */}
      {(canResetPassword || canResetMfa) && (
        <SectionContainer className="sm:shadow border border-gray-300 dark:border-dark-gray-800">
          <SectionHeader
            title="Cognito actions"
            subtitle="Trigger Cognito operations for this user."
          />
          <div className="px-4 py-5 sm:px-6 space-y-4">
            {canResetPassword && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">
                    Reset password
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-gray-400">
                    Sends a temporary password to the user's email via Cognito.
                  </p>
                </div>
                {!confirmResetPassword ? (
                  <button
                    onClick={() => setConfirmResetPassword(true)}
                    className={[
                      'inline-flex items-center px-3 py-2 border border-gray-300 dark:border-dark-gray-500',
                      'text-sm font-medium rounded-md text-gray-700 dark:text-dark-gray-200',
                      'bg-white dark:bg-dark-gray-700 hover:bg-gray-50 dark:hover:bg-dark-gray-600',
                      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                    ].join(' ')}
                  >
                    Reset password
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Confirm?</span>
                    <button
                      onClick={() => setConfirmResetPassword(false)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-dark-gray-500 text-sm font-medium rounded-md text-gray-700 dark:text-dark-gray-200 bg-white dark:bg-dark-gray-700 hover:bg-gray-50 dark:hover:bg-dark-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetPassword}
                      disabled={resettingPassword}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resettingPassword ? 'Sending...' : 'Confirm reset'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {canResetMfa && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">
                    Reset MFA
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-gray-400">
                    Clears the user's MFA enrollment so they must re-enroll.
                  </p>
                </div>
                {!confirmResetMfa ? (
                  <button
                    onClick={() => setConfirmResetMfa(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-dark-gray-500 text-sm font-medium rounded-md text-gray-700 dark:text-dark-gray-200 bg-white dark:bg-dark-gray-700 hover:bg-gray-50 dark:hover:bg-dark-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Reset MFA
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Confirm?</span>
                    <button
                      onClick={() => setConfirmResetMfa(false)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-dark-gray-500 text-sm font-medium rounded-md text-gray-700 dark:text-dark-gray-200 bg-white dark:bg-dark-gray-700 hover:bg-gray-50 dark:hover:bg-dark-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetMfa}
                      disabled={resettingMfa}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resettingMfa ? 'Resetting...' : 'Confirm reset'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </SectionContainer>
      )}

      {/* Danger zone — delete */}
      {canManage && !isSelf && (
        <SectionContainer className="sm:shadow border border-red-200 dark:border-red-900">
          <SectionHeader
            title="Danger zone"
            subtitle="Irreversible actions. Proceed with caution."
            backgroundColor="bg-red-50 dark:bg-red-950"
          />
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">
                  Delete admin user
                </p>
                <p className="text-xs text-gray-500 dark:text-dark-gray-400">
                  Soft-deletes the record and disables their Cognito access.
                </p>
              </div>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white dark:bg-dark-gray-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete user
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Are you sure?</span>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-dark-gray-500 text-sm font-medium rounded-md text-gray-700 dark:text-dark-gray-200 bg-white dark:bg-dark-gray-700 hover:bg-gray-50 dark:hover:bg-dark-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Yes, delete'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </SectionContainer>
      )}
    </>
  );
};

export default AdminUserDetail;
